import React from 'react';
import { useApp } from '../context/AppContext';
import { AppLayer } from '../types';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  Bot,
  Server,
  Terminal,
  Lightbulb,
  Flame,
  Video,
  Image,
  FileText,
  Sparkles,
  Cpu,
  Activity,
  Beer,
  X,
  Compass,
  Sliders
} from 'lucide-react';

interface AllDecksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AllDecksModal: React.FC<AllDecksModalProps> = ({ isOpen, onClose }) => {
  const { activeTab, setActiveTab, setActiveLayer } = useApp();

  if (!isOpen) return null;

  const handleSelectDeck = (tabId: string, layer: AppLayer) => {
    triggerHaptic(25);
    setActiveLayer(layer);
    setActiveTab(tabId);
    onClose();
  };

  const deckSections = [
    {
      title: 'Family Sanctuary & Household Suite 🏡',
      color: 'text-emerald-300',
      layer: 'family' as AppLayer,
      decks: [
        { id: 'cameras', label: 'Perimeter Cameras & Alerts', desc: 'Live Frigate feeds, PTZ, Two-Way Audio, Flashlight, Siren & Alert Clips', icon: <Video className="w-5 h-5 text-emerald-400" /> },
        { id: 'fissal', label: 'Fissal AI Assistant', desc: 'Command Fissal to fix network/server issues, ask questions, or talk live', icon: <Bot className="w-5 h-5 text-amber-400" /> },
        { id: 'devices', label: 'Network Diagnostics & Self-Healing', desc: 'Deep network health checks, one-tap self-healing & household Wi-Fi devices', icon: <Activity className="w-5 h-5 text-sky-400" /> },
        { id: 'living', label: 'Smart Home & Desk Whiteboard', desc: 'Smart lights, dehumidifier climate control & Echo desk notes', icon: <Lightbulb className="w-5 h-5 text-yellow-400" /> },
        { id: 'photos', label: 'Immich Photo Vault', desc: 'Sovereign family photo & video backup status and guide', icon: <Image className="w-5 h-5 text-pink-400" /> },
        { id: 'note', label: 'Echo Desk Whiteboard', desc: 'Post notes, reminders, and messages directly to Echo', icon: <FileText className="w-5 h-5 text-amber-300" /> },
      ]
    },
    {
      title: 'DevOps & In-Field USB Hardware Ops 🛠️',
      color: 'text-fissal-gold',
      layer: 'devops' as AppLayer,
      decks: [
        { id: 'cockpit', label: 'Prime DevOps Cockpit', desc: 'At-a-Glance Server Vitals, PM2 Daemons, Docker & Security Feeds', icon: <Compass className="w-5 h-5 text-fissal-gold" /> },
        { id: 'diagnostics', label: 'USB-C AI Hook & Hardware Bench', desc: 'Hook Fissal AI into devices via Phone USB-C OTG, Shizuku, RF & Audio RTA', icon: <Cpu className="w-5 h-5 text-emerald-400" /> },
        { id: 'fissal', label: 'Fissal Core Co-Pilot', desc: 'Full DevOps & USB Serial Hardware AI Co-Pilot', icon: <Bot className="w-5 h-5 text-fissal-gold" /> },
        { id: 'terminal', label: 'Shizuku & SSH Terminal', desc: 'Direct on-device Shizuku shell & echodedicated SSH terminal', icon: <Terminal className="w-5 h-5 text-emerald-400" /> },
        { id: 'server', label: 'Server Container Matrix', desc: 'Deep Docker container & PM2 process management', icon: <Server className="w-5 h-5 text-sky-400" /> },
        { id: 'devices', label: 'Tactile Field Controller Decks', desc: 'Sound Rig X32, Stage Lighting, Frigate PTZ & USB Serial Rig', icon: <Sliders className="w-5 h-5 text-amber-400" /> },
        { id: 'stagerig', label: 'Stage Rig Control', desc: 'Altman Lekos, Behringer X32 OSC & Soundcheck RTA', icon: <Flame className="w-5 h-5 text-red-500" /> },
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="w-full max-w-lg max-h-[85vh] rounded-3xl bg-slate-950 border border-fissal-gold/40 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-amber-950/40 via-slate-900 to-black border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-fissal-gold shadow-quantum-glow">
              <Compass className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
                REALM & DECK SELECTOR
              </h2>
              <p className="text-[10px] text-slate-400 font-mono">
                Explore & Switch Any Realm Across The Relay
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              triggerHaptic(15);
              onClose();
            }}
            className="p-1.5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Deck List Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {deckSections.map((section) => (
            <div key={section.title} className="space-y-2">
              <div className={`text-[11px] font-mono font-bold tracking-wider uppercase ${section.color}`}>
                {section.title}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {section.decks.map((deck) => {
                  const isCurrent = activeTab === deck.id;
                  return (
                    <button
                      key={deck.id}
                      onClick={() => handleSelectDeck(deck.id, section.layer)}
                      className={`w-full p-3 rounded-2xl border text-left flex items-center justify-between transition-all ${
                        isCurrent
                          ? 'bg-amber-500/20 border-fissal-gold text-white shadow-quantum-glow/30'
                          : 'bg-slate-900/60 hover:bg-slate-800/80 border-white/5 text-slate-300 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                          {deck.icon}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-2">
                            {deck.label}
                            {isCurrent && (
                              <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-fissal-gold text-slate-950 font-bold">
                                ACTIVE
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 leading-snug">
                            {deck.desc}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
